// frontend/src/components/admin/content/LessonFormModal.tsx

import { useState, FormEvent } from 'react';
import { adminService } from '../../../services/admin.service';
import { AdminLesson } from '../../../types/admin.types';

import { VideoEmbedPlayer } from '../../common/VideoEmbedPlayer';

interface LessonFormModalProps {
  courseId: string;
  lesson?: AdminLesson | null;
  onClose: () => void;
  onSaved: () => void;
}

export function LessonFormModal({ courseId, lesson, onClose, onSaved }: LessonFormModalProps) {
  const [title, setTitle] = useState(lesson?.title || '');
  const [topic, setTopic] = useState(lesson?.topic || 'Genel');
  const [content, setContent] = useState(lesson?.content || '');
  const [videoUrl, setVideoUrl] = useState(lesson?.video_url || '');
  const [xpReward, setXpReward] = useState(lesson?.xp_reward || 50);
  const [orderIndex, setOrderIndex] = useState(lesson?.order_index || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!lesson;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        title,
        topic,
        content: content || null,
        video_url: videoUrl || undefined,
        xp_reward: xpReward,
        order_index: orderIndex,
      };

      if (isEditing && lesson) {
        await adminService.updateLesson(lesson.id, payload);
      } else {
        await adminService.createLesson({
          course_id: courseId,
          ...payload,
          content: content,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ders kaydedilemedi.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <h3>{isEditing ? 'Dersi Düzenle' : 'Yeni Ders Ekle'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Başlık</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ders başlığını girin" required />
          </div>

          <div className="form-group">
            <label>Ders Konusu (Örn: Döngüler, Veri Tipleri)</label>
            <input className="input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Ders konusunu girin (Varsayılan: Genel)" />
          </div>

          <div className="form-group">
            <label>İçerik (varsayılan açıklamalar)</label>
            <textarea
              className="input"
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Dersin giriş/temel açıklaması. Boş bırakırsanız AI hoca doğrudan konulardan devam eder."
            />
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label>Ders Sıra No (Order Index)</label>
              <input
                className="input"
                type="number"
                value={orderIndex}
                onChange={(e) => setOrderIndex(Number(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>XP Ödülü</label>
              <input
                className="input"
                type="number"
                min={10}
                value={xpReward}
                onChange={(e) => setXpReward(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Video URL (Opsiyonel, YouTube, Vimeo, Google Drive veya MP4 bağlantısı)</label>
            <input
              className="input"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {videoUrl && videoUrl.trim().length > 5 && (
              <div style={{ marginTop: 12, padding: 12, background: 'rgba(0,174,239,0.05)', borderRadius: 12, border: '1px dashed rgba(0,174,239,0.3)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>
                  📹 Video Önizleme:
                </div>
                <div style={{ maxWidth: 480, margin: '0 auto' }}>
                  <VideoEmbedPlayer url={videoUrl} title="Önizleme" showExternalLink={false} />
                </div>
              </div>
            )}
          </div>

          {error && <p className="auth-error">{error}</p>}

          <div className="admin-modal-actions">
            <button type="button" className="lesson-btn secondary" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="lesson-btn primary" disabled={isSaving}>
              {isSaving ? 'Kaydediliyor...' : isEditing ? 'Güncelle' : 'Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
